import { Colors } from "@blueprintjs/core";
import Color from "color";
import { getColorPaletteAtIndex, getOverlayShapeAtIndex } from "config";
import { PortfolioFund } from "contorller/portfolio/types";
import { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { Datum, Layout, PlotData } from "plotly.js";
import { PACHistogramOptions } from "./pac";

interface SimulationOptions {
  selectedHistogramIndex?: number;
  showMeanPerfomance?: boolean;
  opacity?: number;
  isPrinting?: boolean;
  window?: { start: DateTime; end: DateTime };
  isPACProtected?: boolean;
}

export const isSafari =
  /constructor/i.test((window as any).HTMLElement) ||
  (function (p) {
    return p.toString() === "[object SafariRemoteNotification]";
  })(
    !(window as any)["safari"] ||
    (typeof (safari as any) !== "undefined" &&
      (window as any)["safari"].pushNotification)
  );

export const A4_RATIO = 1.4142;
export const PRINTABLE_GRAPH_WIDTH = isSafari ? 1095 : 1170;
export const PRINTABLE_STAT_TRIO = 768;
export const PRINTABLE_ACCORDION_WIDTH = isSafari ? 1100 : 1175;
export const PRINTABLE_HEIGHT = PRINTABLE_GRAPH_WIDTH * A4_RATIO;

export function createSimulationHistograms(
  histogram: { absolute: number; start: Date; end: Date }[],
  colors: {
    positive: (selected: boolean) => string;
    negative: (selected: boolean) => string;
  },
  t: TFunction,
  options?: SimulationOptions
): Partial<PlotData>[] {
  const totalCount = histogram.length;
  const hiddenOpacity = options?.opacity ?? 0.5;
  const positiveCount = histogram.filter((f) => f.absolute >= 0).length;
  const negativeCount = totalCount - positiveCount;
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  } as Intl.DateTimeFormatOptions;

  let xWindowOpacityStart = options?.window?.start?.toJSDate();
  let xWindowOpacityEnd = options?.window?.end?.toJSDate();
  let opacity = new Array(histogram.length);
  let x = new Array<Date>(histogram.length);
  let yPositive = new Array(histogram.length);
  let textPositive = new Array(histogram.length);
  let yNegative = new Array(histogram.length);
  let textNegative = new Array(histogram.length);
  let yMeanSum = 0;
  let hasSelectedIndex = options?.selectedHistogramIndex != null;
  histogram.forEach((e, index) => {
    x[index] = e.start;
    opacity[index] =
      !hasSelectedIndex || options?.selectedHistogramIndex === index
        ? 1.0
        : 0.65;
    if (xWindowOpacityStart && xWindowOpacityEnd) {
      if (e.start < xWindowOpacityStart || e.end > xWindowOpacityEnd) {
        opacity[index] = hiddenOpacity / 2.0;
      }
    }
    yPositive[index] = e.absolute >= 0 ? e.absolute * 100 : null;
    textPositive[index] = `${(e.absolute * 100).toFixed(
      2
    )}% ( ${e.start.toLocaleDateString(
      "it",
      dateOptions
    )} - ${e.end.toLocaleDateString("it", dateOptions)} )`;
    yNegative[index] = e.absolute < 0 ? e.absolute * 100 : null;
    textNegative[index] = `${(e.absolute * 100).toFixed(
      2
    )}% ( ${e.start.toLocaleDateString(
      "it",
      dateOptions
    )} - ${e.end.toLocaleDateString("it", dateOptions)} )`;
    yMeanSum += e.absolute * 100;
  });

  // const x = histogram.map((e) => e.start);

  const meanPerfomance = yMeanSum / histogram.length;

  const otherPlots: Partial<PlotData>[] = options?.showMeanPerfomance
    ? [
      {
        type: "scatter",
        mode: "lines",
        x,
        line: {
          color: Color(Colors.LIME3).alpha(0.8).toString(),
          dash: "dash",
        },
        showlegend: false,
        hoverinfo: "none",
        y: new Array(histogram.length).fill(meanPerfomance),
        ...(options.isPACProtected === true ? {
          yaxis: meanPerfomance > 0 ? "y2" : "y"
        } : {})
      },
    ]
    : [];

  if (options?.isPACProtected === true) {
    // const sortedX = x.sort((a, b) => a.getTime() - b.getTime());
    // const minDate = sortedX[0];
    // const maxDate = sortedX[x.length - 1];

    otherPlots.push(
      {
        type: "scatter",
        mode: "none",

        x: [
          x[0],
          x[x.length - 1],
          x[x.length - 1],
          x[0],
        ],

        y: [0, 0, 1, 1],

        yaxis: "y3",

        fill: "toself",

        fillcolor: "#00000000",
        hoverinfo: "text",
        text: "&nbsp;&nbsp;&nbsp;&nbsp;Quest'area simboleggia, senza quantificare o qualificare, le coperture&nbsp;&nbsp;&nbsp;&nbsp;<br>&nbsp;&nbsp;&nbsp;&nbsp;assicurative a tutela del progetto. Usa il menù <b>Impostazione Massimali</b>,&nbsp;&nbsp;&nbsp;&nbsp;<br>&nbsp;&nbsp;&nbsp;&nbsp;disponibile nella spalla destra, per specificare le tue preferenze.&nbsp;&nbsp;&nbsp;&nbsp;",
        hoverlabel: {
          bgcolor: "#2d2a3d",
          bordercolor: "#2d2a3d",
          font: {
            color: "#fff",
            size: 12,
          },

          // align: "left",
        },
        showlegend: false,
      },
    )
  }
  const caseLabel = (count: number) => (count > 1 ? t("casi") : t("caso"));
  const positiveLabel = (count: number) =>
    count > 1 ? t("Positivi") : t("Positivo");
  const negativeLabel = (count: number) =>
    count > 1 ? t("Negativi") : t("Negativo");
  return [
    {
      type: "bar",
      name: `${positiveLabel(positiveCount)} ${positiveCount} ${caseLabel(
        positiveCount
      )} (<span style="color: ${colors.positive(true)};">${(
        (positiveCount / totalCount) *
        100
      ).toFixed(1)}%</span>)`,
      x,
      y: yPositive,
      ...(options?.isPACProtected === true ? {
        yaxis: "y2",
      } : {}),
      textposition: "none",
      hoverinfo: options?.isPrinting ? "none" : "text",
      text: textPositive,
      marker: {
        opacity,
        color: colors.positive(true),
      },
    },
    {
      type: "bar",
      name: `${negativeLabel(negativeCount)} ${negativeCount} ${caseLabel(
        negativeCount
      )} (<span style="color: ${colors.negative(true)};">${(
        (negativeCount / totalCount) *
        100
      ).toFixed(1)}%</span>)`,
      x,
      y: yNegative,
      ...(options?.isPACProtected === true ? {
        yaxis: "y",
      } : {}),
      hoverinfo: options?.isPrinting ? "none" : "text",
      textposition: "none",
      text: textNegative,
      marker: {
        opacity,
        color: colors.negative(true),
      },
    },
    ...otherPlots,
  ];
}

export function getColorFn(kind: "positive" | "negative") {
  if (kind === "positive") {
    return (selected: boolean) =>
      Color(Colors.BLUE3)
        .alpha(selected ? 1 : 0.7)
        .string();
  } else {
    return (selected: boolean) =>
      Color(Colors.RED3)
        .alpha(selected ? 1 : 0.7)
        .string();
  }
}

export function getRecurrencyInvString(monthFrequency: number): string {
  switch (monthFrequency) {
    case 0:
    case 1:
      return "Mensile";
    case 2:
      return "Trimestrale";
    case 5:
      return "Semestrale";
    case 11:
      return "Annuale";
    case 15:
      return "Quindicinale";
    default:
      return "";
  }
}

export function getStartYear(startUnix: number): string {
  if (startUnix == 0) {
    return "dall'inizio";
  }
  var t = new Date(startUnix * 1000);
  return t.getFullYear().toString();
}

export function getEndYear(startUnix: number): string {
  if (startUnix == 0) {
    return "alla fine";
  }
  var t = new Date(startUnix * 1000);
  return t.getFullYear().toString();
}

export function developCuttedHistoricalFromGraphLine(graphLine: Partial<PlotData>, cuttingStartDate: DateTime, cuttingEndDate: DateTime): number[] {
  const developedHist: { date: DateTime, value: number }[] = [];
  const undevelopedHist: { x: Datum, y: Datum }[] =
    ((graphLine.x ?? []) as Datum[]).map((value, index) => { return { x: value, y: ((graphLine.y ?? []) as Datum[])[index] } });

  for (const graphPoint of undevelopedHist) {
    const currentDate = DateTime.fromMillis((graphPoint.x ?? [])?.valueOf() as number ?? 0);
    if (currentDate >= cuttingStartDate && currentDate < cuttingEndDate) {
      if (developedHist.length <= 0) {
        developedHist.push({ date: currentDate, value: graphPoint.y as unknown as number });
        continue;
      }
      while (currentDate.diff(developedHist[developedHist.length - 1].date, "days").days >= 2) { // >1 pick also 1d 1h 
        developedHist.push({ date: developedHist[developedHist.length - 1].date.plus({ days: 1 }), value: developedHist[developedHist.length - 1].value });
      }
      developedHist.push({ date: currentDate, value: graphPoint.y as unknown as number });

    } else if (currentDate > cuttingEndDate) {
      break;
    }
  }


  return developedHist.map(dh => dh.value)
}

export function getHistogramPercentage(min: number, max: number, value: number, expandPercentage?: number): number {
  const exMin = min - Math.abs(min * (expandPercentage ?? 0))
  // const exMax = max + Math.abs(max * (expandPercentage ?? 0))
  return ((value - exMin) / (max - exMin)) * 100;
}

export function getOngoingCharges(funds: PortfolioFund[]): number {
  if (funds.filter(f => (f.fund.valueCost ?? 0) === 0).length > 0) {
    return 0;
  }
  const ongoingCharges = funds.reduce((sum, fund) => {
    if (fund.percentage > 0) {
      return sum + (fund.percentage * ((fund.fund.valueCost ?? 0) / 100));
    }
    return sum;
  }, 0);
  return ongoingCharges;
}

export function getValueGradientPercentage(value: number, allValues: number[], betterHigher: boolean): number {
  const quote = 100 / (allValues?.length ?? 1);
  const index = allValues.toSorted((a, b) => betterHigher ? a - b : b - a).findIndex((v) => v === value);
  return index * quote
}

export function calculateCommonDtick(maxRange: number): number {
  if (maxRange <= 0) {
    return 1;
  }

  const targetTickCount = 5;

  const roughStep = maxRange / targetTickCount;

  const magnitude = Math.pow(
    10,
    Math.floor(Math.log10(roughStep))
  );

  const normalized = roughStep / magnitude;

  let niceStep: number;

  if (normalized <= 1) {
    niceStep = 1;
  } else if (normalized <= 2) {
    niceStep = 2;
  } else if (normalized <= 5) {
    niceStep = 5;
  } else {
    niceStep = 10;
  }

  return niceStep * magnitude;
}

export function buildPACHistogramLayout(
  baseLayout: Partial<Layout>,
  options: PACHistogramOptions
): Partial<Layout> {
  const gapRatio = Math.min(
    Math.max(options.gapRatio, 0),
    0.5
  );
  const maxPositive = Math.max(...(options.positiveRange ?? [0]));
  const absMinNegative = Math.abs(Math.min(...(options.negativeRange ?? [0])));
  const totalRange = maxPositive + absMinNegative;
  const availableDomain = 1 - gapRatio;
  // let positiveDomainSize = 0;
  let negativeDomainSize = 0;
  if (totalRange > 0) {
    // positiveDomainSize =
    //   availableDomain *
    //   (maxPositive / totalRange);

    negativeDomainSize =
      availableDomain *
      (absMinNegative / totalRange);
  }
  // const domainSize = (1 - gapRatio) / 2;

  const lowerDomain: [number, number] = [
    0,
    negativeDomainSize,
  ];

  const upperDomain: [number, number] = [
    negativeDomainSize + gapRatio,
    1,
  ];

  const gapBottom = negativeDomainSize;
  const gapTop = negativeDomainSize + gapRatio;
  const gapCenter = (gapBottom + gapTop) / 2;

  return {
    ...baseLayout,

    yaxis: {
      showgrid:
        baseLayout.yaxis?.showgrid ?? true,

      gridcolor:
        baseLayout.yaxis?.gridcolor,

      gridwidth:
        baseLayout.yaxis?.gridwidth,

      zeroline: true,

      zerolinecolor: "#fff",// intent success minimal text color

      zerolinewidth:
        3,

      tickfont:
        baseLayout.yaxis?.tickfont,

      tickformat:
        baseLayout.yaxis?.tickformat,

      ticks:
        baseLayout.yaxis?.ticks,

      ticklen:
        baseLayout.yaxis?.ticklen,

      tickwidth:
        baseLayout.yaxis?.tickwidth,

      title:
        baseLayout.yaxis?.title,

      showticklabels:
        baseLayout.yaxis?.showticklabels ?? true,

      domain: lowerDomain,

      range: options.negativeRange,

      autorange: false,

      // Questo asse è completamente indipendente
      overlaying: undefined,

      anchor: "x",

      side: "left",

      dtick: options.dtick,
    },

    yaxis2: {
      // Prendiamo solo le proprietà estetiche utili
      // dall'asse originale
      showgrid:
        baseLayout.yaxis?.showgrid ?? true,

      gridcolor:
        baseLayout.yaxis?.gridcolor,

      gridwidth:
        baseLayout.yaxis?.gridwidth,

      zeroline: true,

      zerolinecolor: "#fff",// intent success minimal text color

      zerolinewidth:
        3,

      tickfont:
        baseLayout.yaxis?.tickfont,

      tickformat:
        baseLayout.yaxis?.tickformat,

      ticks:
        baseLayout.yaxis?.ticks,

      ticklen:
        baseLayout.yaxis?.ticklen,

      tickwidth:
        baseLayout.yaxis?.tickwidth,

      title:
        baseLayout.yaxis?.title,

      showticklabels:
        baseLayout.yaxis?.showticklabels ?? true,

      domain: upperDomain,

      range: options.positiveRange,

      autorange: false,

      side: "left",

      anchor: "x",

      // IMPORTANTISSIMO:
      // non deve essere "overlaying": "y"
      overlaying: undefined,
      dtick: options.dtick,
    },
    yaxis3: {
      domain: [
        negativeDomainSize,
        negativeDomainSize + gapRatio,
      ],

      range: [0, 1],

      visible: false,

      fixedrange: true,

      anchor: "x",

      overlaying: undefined,
    },
    shapes: [
      ...(baseLayout.shapes ?? []),
      // ...createMetallicGoldShapes(
      //   negativeDomainSize,
      //   gapRatio,
      //   93,
      // ),
    ],
    images: [
      createMetallicReflection(
        negativeDomainSize,
        gapRatio,
      ),
    ],
    annotations: [
      {
        xref: "paper",
        yref: "paper",

        x: 0.5,
        y: gapCenter,

        text: options.gapText,

        showarrow: false,

        font: {
          color: options.gapTextColor,
          size: 15,
          weight: 600,
        },

        xanchor: "center",
        yanchor: "middle",

        align: "center",

        // layer: "above",
      }
    ],
    // legend: {
    //   orientation: "h",
    //   "entrywidth": 0.5,
    //   entrywidthmode: "fraction",

    // }
  };
}

export function createAreaBetween(
  x: Date[],
  values: number[],
  reference: number[],
  index: number,
  legendGroup: string,
): Partial<PlotData> {
  return {
    type: "scatter",
    mode: "lines",

    x: [
      ...x,
      ...[...x].reverse(),
    ],

    y: [
      ...values,
      ...[...reference].reverse(),
    ],

    fill: "toself",

    fillcolor: "rgba(0,0,0,0)",

    fillpattern: {
      shape: getOverlayShapeAtIndex(index),
      size: 8,
      solidity: 0.2,
      fgcolor: getColorPaletteAtIndex(index),
      bgcolor: "rgba(0,0,0,0)",
      fillmode: "overlay",
    },

    line: {
      color: "transparent",
      width: 0,
    },

    hoverinfo: "skip",

    showlegend: false,
    legendgroup: legendGroup,
  };
}

export function getMaximumName(index: number): string {
  switch (index) {
    case 0:
      return "P. Caso Morte";
    case 1:
      return "P. Invalidità Permanente";
    case 2:
      return "P. Non Autosufficienza";
    case 3:
      return "P. Sanitaria";
    case 4:
      return "P. Casa";
    default:
      return "";
  }
}


// const createMetallicGoldShapes = (
//   negativeDomainSize: number,
//   gapRatio: number,
//   bands = 31,
// ): Partial<Shape>[] => {
//   // Colori principali del gradiente metallico
//   const stops = [
//     { position: 0.00, color: [92, 65, 0] },      // oro molto scuro
//     { position: 0.15, color: [166, 120, 5] },    // oro scuro
//     { position: 0.30, color: [205, 164, 45] },   // oro
//     { position: 0.42, color: [238, 210, 103] },  // oro chiaro
//     { position: 0.50, color: [255, 246, 190] },  // highlight
//     { position: 0.58, color: [238, 210, 103] },  // oro chiaro
//     { position: 0.70, color: [205, 164, 45] },   // oro
//     { position: 0.85, color: [166, 120, 5] },   // oro scuro
//     { position: 1.00, color: [92, 65, 0] },      // oro molto scuro
//   ];

//   const interpolateColor = (position: number): string => {
//     let left = stops[0];
//     let right = stops[stops.length - 1];

//     for (let i = 0; i < stops.length - 1; i++) {
//       if (
//         position >= stops[i].position &&
//         position <= stops[i + 1].position
//       ) {
//         left = stops[i];
//         right = stops[i + 1];
//         break;
//       }
//     }

//     const range = right.position - left.position;
//     const t =
//       range === 0
//         ? 0
//         : (position - left.position) / range;

//     const r = Math.round(
//       left.color[0] + (right.color[0] - left.color[0]) * t,
//     );

//     const g = Math.round(
//       left.color[1] + (right.color[1] - left.color[1]) * t,
//     );

//     const b = Math.round(
//       left.color[2] + (right.color[2] - left.color[2]) * t,
//     );

//     return `rgb(${r}, ${g}, ${b})`;
//   };

//   return Array.from({ length: bands }, (_, index) => {
//     const start = index / bands;
//     const end = (index + 1) / bands;

//     return {
//       type: "rect",
//       xref: "paper",
//       yref: "paper",

//       x0: 0,
//       x1: 1,

//       y0: negativeDomainSize + gapRatio * start,
//       y1: negativeDomainSize + gapRatio * end,

//       fillcolor: interpolateColor((start + end) / 2),

//       line: {
//         width: 0,
//       },

//       layer: "below",
//     };
//   });
// };

const createMetallicReflection = (
  negativeDomainSize: number,
  gapRatio: number,
): Partial<Plotly.Layout["images"][number]> => {
  const svg = `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1200"
      height="120"
      viewBox="0 0 1200 120"
      preserveAspectRatio="none"
    >

      <!-- BASE BLU SCURO -->
      <rect
        x="0"
        y="0"
        width="1200"
        height="120"
        fill="#383547"
      />


      <!-- ============================================= -->
      <!-- RIFLESSO CENTRALE AMPIO                           -->
      <!-- ============================================= -->

      <polygon
        points="250,120 900,120 950,0 300,0"
        fill="#2d2a3d"
      />

      <!-- ============================================= -->
      <!-- OMBRE LATERALI DELLA BANDA                    -->
      <!-- ============================================= -->

      <polygon
        points="130,120 270,120 320,0 180,0"
        fill="rgba(10,10,18,0.25)"
      />

      <polygon
        points="880,120 1020,120 1070,0 930,0"
        fill="rgba(10,10,18,0.25)"
      />

      <!-- ============================================= -->
      <!-- LEGGERA SFUMATURA VERTICALE                   -->
      <!-- ============================================= -->

      <rect
        x="0"
        y="0"
        width="1200"
        height="120"
        fill="url(#metalShade)"
      />

      <defs>

        <linearGradient
          id="metalShade"
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >

          <stop
            offset="0%"
            stop-color="#11101c"
            stop-opacity="0.18"
          />

          <stop
            offset="25%"
            stop-color="#2d2a3d"
            stop-opacity="0"
          />

          <stop
            offset="55%"
            stop-color="#ffffff"
            stop-opacity="0.06"
          />

          <stop
            offset="75%"
            stop-color="#2d2a3d"
            stop-opacity="0"
          />

          <stop
            offset="100%"
            stop-color="#11101c"
            stop-opacity="0.20"
          />

        </linearGradient>

      </defs>

    </svg>
  `;

  return {
    source: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,

    xref: "paper",
    yref: "paper",

    x: 0,
    y: negativeDomainSize + gapRatio,

    sizex: 1,
    sizey: gapRatio,

    xanchor: "left",
    yanchor: "top",

    sizing: "stretch",

    layer: "below",

    opacity: 1,
  };
};